import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { customerApi, setCustomerToken, hasCustomerToken } from "@/lib/customerApi";
import { storeApi } from "@/lib/storeApi";
import StoreTopbar from "../StoreTopbar";
import HubFooter from "../HubFooter";
import PlanSection from "./PlanSection";
import PicksSection from "./PicksSection";
import MyBots from "./MyBots";
import DashboardHeader from "./DashboardHeader";
import PerformanceSection from "./PerformanceSection";
import { pixel } from "@/lib/metaPixel";

export default function AccountDashboard() {
  const nav = useNavigate();
  const loc = useLocation();
  const here = loc.pathname + loc.search;
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const pick = params.get("pick");
  const sessionId = params.get("session_id");

  const load = useCallback(() => customerApi.get("/store/account/me").then((r) => setMe(r.data)), []);

  useEffect(() => {
    if (!hasCustomerToken()) {
      nav(`/account/login?next=${encodeURIComponent(here.startsWith("/account/login") ? "/account" : here)}`, { replace: true });
      return;
    }
    load().catch(() => { setCustomerToken(null); nav("/account/login?next=/account"); });
    storeApi.get("/store/mt5/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, [load, nav]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sessionId) return undefined;
    let n = 0;
    setCheckout("checking");
    const t = setInterval(async () => {
      n += 1;
      try {
        const { data } = await customerApi.get(`/store/account/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearInterval(t);
          if (data.amount != null) pixel.purchase(sessionId, data.tier_name, data.amount, data.currency);
          setCheckout("paid");
          await load();
          params.delete("session_id");
          setParams(params, { replace: true });
        } else if (n > 15) { clearInterval(t); setCheckout("pending"); }
      } catch { if (n > 15) { clearInterval(t); setCheckout("pending"); } }
    }, 2000);
    return () => clearInterval(t);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePrefs = async () => setMe((await customerApi.post("/store/account/preferences", { marketing_opt_in: !me.marketing_opt_in })).data);
  const logout = () => { setCustomerToken(null); nav("/mt5"); };

  return (
    <div data-testid="account-dashboard" className="min-h-screen bg-[#050914] text-[#F1F5F9]">
      <StoreTopbar subtitle="Your account" />
      <main className="max-w-[1100px] mx-auto px-6 py-10 space-y-6">
        {!me ? <div className="mono text-xs text-[#64748B]">Loading…</div> : (
          <>
            <DashboardHeader me={me} />
            <div className="flex flex-wrap items-center justify-end gap-4 text-xs">
              <label className="flex items-center gap-2 cursor-pointer text-[#94A3B8]">
                <input type="checkbox" checked={!!me.marketing_opt_in} onChange={togglePrefs} data-testid="account-marketing-toggle" className="accent-[#FBBF24]" />
                Email me news + updates
              </label>
              <button onClick={logout} data-testid="account-logout-btn" className="mono text-[10px] uppercase tracking-widest text-[#94A3B8] hover:text-white">Log out</button>
            </div>
            {checkout === "checking" && <div data-testid="checkout-checking" className="text-sm text-[#38BDF8]">Confirming your payment…</div>}
            {checkout === "paid" && <div data-testid="checkout-paid" className="text-sm text-[#2ECA7F]">Payment confirmed - welcome aboard! Pick your bots below.</div>}
            {checkout === "pending" && <div data-testid="checkout-pending" className="text-sm text-[#FBBF24]">Payment still processing - refresh in a minute.</div>}
            {me.subscription?.active && <PerformanceSection me={me} />}
            <PlanSection me={me} preselect={params.get("plan")} />
            <PicksSection key={`${me.subscription?.tier}-${me.picks.length}`} me={me} catalog={catalog} preselect={pick} onSaved={setMe} />
            <MyBots me={me} onChange={setMe} />
          </>
        )}
      </main>
      <HubFooter />
    </div>
  );
}
